import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Image,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX, resolveOptionSwatchThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {CUSTOMIZE_STEP_LAYOUT_KIND} from '@amblique/king-living/app/hooks/use-customize-steps';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {
  formatShelvesSelectionName,
  resolveOptionProductName,
  resolveOptionProductShortDescriptionPlain,
} from '@amblique/king-living/app/utils/product-utils';

/**
 * Inline content for a single radio option: thumbnail + name lines + radio indicator.
 * Thumbnail, extra price, View details: `ConfigurationSelector` option-step theme parts.
 * Optionally shows "View details" link on mobile when onViewDetails is provided (multi-slot context).
 * Optionally shows "+ $X" extra price below name when extraPrice > 0.
 *
 * shortDescription → name1 (light); product name → name2 (bold). Name-only → name2.
 */
function RadioOptionNameBlock({
  displayPrimary,
  isOutOfStock,
  line2Trimmed,
  onViewDetails,
  shortDescription,
  showSecondaryNameLine,
  singleTypeTitle,
  styles,
  useSingleTypeTitleLayout,
  useListLayout,
  handleViewDetails,
}) {
  return (
    <Flex
      className="radio-option-content-name-container"
      flex={1}
      flexDirection="column"
      minWidth={0}
      gap="8px"
      maxWidth={useListLayout ? undefined : {base: '142px', lg: '246px'}}
    >
      {useSingleTypeTitleLayout ? (
        <Text className="radio-option-content-name2" {...styles?.nameLine2}>
          {singleTypeTitle}
        </Text>
      ) : showSecondaryNameLine ? (
        <>
          <Text className="radio-option-content-name1" {...styles?.nameLine1}>
            {displayPrimary}
          </Text>
          <Text className="radio-option-content-name2" {...styles?.nameLine2}>
            {line2Trimmed}
          </Text>
        </>
      ) : (
        <Text className="radio-option-content-name2" {...styles?.nameLine2}>
          {displayPrimary}
        </Text>
      )}
      {useSingleTypeTitleLayout && shortDescription && (
        <Text className="radio-option-content-description" {...styles?.radioOptionDescription}>
          {shortDescription}
        </Text>
      )}
      {isOutOfStock && (
        <Text className="radio-option-content-oos" color="red.500" fontSize="xs">
          <FormattedMessage
            defaultMessage="Sold out"
            id="customize_panel.option_step_radio.sold_out"
          />
        </Text>
      )}
      {onViewDetails && (
        <Text
          as="span"
          className="radio-option-content-view-details"
          {...styles?.radioOptionViewDetails}
          onClick={handleViewDetails}
        >
          <FormattedMessage
            defaultMessage="View details"
            id="configuration_selector.tile.view_details"
          />
        </Text>
      )}
    </Flex>
  );
}

function RadioOptionExtraPrice({currency, extraPrice, intl, listPrice, styles}) {
  if (!(extraPrice > 0)) return null;

  return (
    <Box className="radio-option-content-extra-price-container" {...styles?.radioOptionExtraPriceContainer}>
      <Text className="radio-option-content-extra-price" {...styles?.radioOptionExtraPrice}>
        <Box as="span" display="inline" whiteSpace="nowrap">
          <Box as="span" display="inline">
            +{' '}
          </Box>
          <Box as="span" display="inline">
            {formatCurrencyHideTrailingZeroCents(intl, extraPrice, currency)}
          </Box>
        </Box>
        {listPrice != null && listPrice > extraPrice && (
          <>
            {' '}
            <Box as="span" display="inline" textDecoration="line-through">
              {formatCurrencyHideTrailingZeroCents(intl, listPrice, currency)}
            </Box>
          </>
        )}
      </Text>
    </Box>
  );
}

function RadioOptionIndicator({hideRadioIndicator, isSelected, styles}) {
  if (hideRadioIndicator) return null;

  return (
    <Box aria-hidden className="radio-option-indicator" {...styles?.radioIndicator}>
      {isSelected && <Box className="radio-option-indicator__dot" {...styles?.radioIndicatorDot} />}
    </Box>
  );
}

function RadioOptionThumbnail({imageUrl, isFlyoutPicker, product, styles}) {
  if (imageUrl) {
    return (
      <Image
        alt={product.name}
        className="radio-option-content-image"
        htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
        htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
        loading="lazy"
        src={imageUrl}
        {...styles?.radioOptionImage}
      />
    );
  }

  if (!isFlyoutPicker) return null;

  return (
    <Box
      aria-hidden
      className="radio-option-content-image radio-option-content-image--placeholder"
      role="presentation"
      {...styles?.radioOptionImagePlaceholder}
    />
  );
}

export function RadioOptionContent({
  customizeStepKind = 'other',
  extraPrice,
  hideRadioIndicator = false,
  isFlyoutPicker = false,
  isOutOfStock = false,
  listPrice,
  isSelected,
  onViewDetails,
  product,
  containerId,
  styles,
  useSingleTypeTitleLayout = false,
  useListLayout = false,
}) {
  const intl = useIntl();
  const imageUrl = resolveOptionSwatchThumbnailUrl(product);
  const shortDescriptionPlain = resolveOptionProductShortDescriptionPlain(product);
  const productNameRaw = resolveOptionProductName(product);
  const productName =
    customizeStepKind === CUSTOMIZE_STEP_LAYOUT_KIND.MULTI_TYPE ?
      formatShelvesSelectionName(productNameRaw) :
      productNameRaw;
  const displayPrimary = shortDescriptionPlain || productName;
  const line2Trimmed = shortDescriptionPlain ? productName : '';
  const singleTypeTitle = useSingleTypeTitleLayout ? productName || shortDescriptionPlain || '' : null;
  const showSecondaryNameLine = !useSingleTypeTitleLayout && Boolean(shortDescriptionPlain && productName);
  const currency = product.currency ?? 'USD';

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(product);
  };

  const nameBlockProps = {
    displayPrimary,
    handleViewDetails,
    isOutOfStock,
    line2Trimmed,
    onViewDetails,
    shortDescription: useSingleTypeTitleLayout && !isFlyoutPicker ? shortDescriptionPlain : null,
    showSecondaryNameLine,
    singleTypeTitle,
    styles,
    useSingleTypeTitleLayout,
    useListLayout: useListLayout || isFlyoutPicker,
  };

  if (isFlyoutPicker || useListLayout) {
    return (
      <Flex alignItems="center" className="radio-option-content-container" width="100%" gap={4}>
        <RadioOptionThumbnail
          imageUrl={imageUrl}
          isFlyoutPicker={isFlyoutPicker}
          product={product}
          styles={styles}
        />
        <RadioOptionNameBlock {...nameBlockProps} />
        <RadioOptionExtraPrice
          currency={currency}
          extraPrice={extraPrice}
          intl={intl}
          listPrice={listPrice}
          styles={styles}
        />
        <RadioOptionIndicator hideRadioIndicator={hideRadioIndicator} isSelected={isSelected} styles={styles} />
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      className="radio-option-content-container"
      justifyContent="space-between"
      width="100%"
      gap={4}
    >
      <Flex alignItems="center" justifyContent="flex-start" gap={4}>
        <RadioOptionThumbnail
          imageUrl={imageUrl}
          isFlyoutPicker={isFlyoutPicker}
          product={product}
          styles={styles}
        />
        <RadioOptionNameBlock {...nameBlockProps} />
      </Flex>
      <Flex alignItems="center" justifyContent="flex-end" gap={4}>
        <RadioOptionExtraPrice
          currency={currency}
          extraPrice={extraPrice}
          intl={intl}
          listPrice={listPrice}
          styles={styles}
        />
        <RadioOptionIndicator hideRadioIndicator={hideRadioIndicator} isSelected={isSelected} styles={styles} />
      </Flex>
    </Flex>
  );
}

RadioOptionContent.displayName = 'RadioOptionContent';

RadioOptionContent.propTypes = {
  customizeStepKind: PropTypes.string,
  extraPrice: PropTypes.number,
  hideRadioIndicator: PropTypes.bool,
  isFlyoutPicker: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  listPrice: PropTypes.number,
  isSelected: PropTypes.bool,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
  containerId: PropTypes.string,
  styles: PropTypes.object,
  useSingleTypeTitleLayout: PropTypes.bool,
  useListLayout: PropTypes.bool,
};
